import { isMachuRetreatPubliclyListed } from '@/lib/machuRetreatLaunch'

/**
 * Keyword-triggered rows in the header search dropdown.
 * - featured: marketing / hero destinations (shown first)
 * - general: account & self-service pages (shown after featured)
 */
export type SearchAutocompletePromotionTier = 'featured' | 'general'

export type SearchAutocompletePromotion = {
  id: string
  tier: SearchAutocompletePromotionTier
  /** If the normalized query includes any of these (substring), the row is shown */
  matchSubstrings: string[]
  title: string
  subtitle?: string
  href: string
}

export const SEARCH_AUTOCOMPLETE_PROMOTIONS: SearchAutocompletePromotion[] = [
  // --- Featured ---
  {
    id: 'the-retreat-hawaii',
    tier: 'featured',
    matchSubstrings: ['the retreat', 'retreat hawaii', 'hawaii retreat'],
    title: 'The Retreat — Hawaii 2026',
    subtitle: 'Magic convention experience',
    href: '/magic-conventions/the-retreat-hawaii-2026',
  },
  {
    id: 'masterclass',
    tier: 'featured',
    matchSubstrings: ['masterclass', 'master class'],
    title: 'Vanishing Inc. Masterclass',
    subtitle: 'Live lectures & streaming library',
    href: '/masterclass',
  },
  {
    id: 'discoverie',
    tier: 'featured',
    matchSubstrings: ['discoverie', 'discover ie', 'book search tool'],
    title: 'Discoverie',
    subtitle: 'Search tricks & moves across our book library',
    href: '/discoverie',
  },
  {
    id: 'stack-trainer',
    tier: 'featured',
    matchSubstrings: [
      'stack trainer',
      'stack training',
      'memorize stack',
      'stack memorization',
      'memdeck',
      'mem deck',
    ],
    title: 'Stack Trainer',
    subtitle: 'Practice memorized deck order',
    href: '/stack-trainer',
  },
  {
    id: 'hidden-barcode-generator',
    tier: 'featured',
    matchSubstrings: [
      'barcode generator',
      'gimmicked deck barcode',
      'deck barcode creator',
      'hidden barcode',
      'hidden word barcode',
      'barcode art',
    ],
    title: 'Gimmicked Deck Barcode Creator',
    subtitle: 'Hide a deck name inside printable barcode artwork',
    href: '/hidden-barcode-generator',
  },
  {
    id: 'magic-book-reviews',
    tier: 'featured',
    matchSubstrings: [
      'magic book reviews',
      'magic book review',
      'vi book reviews',
      'vanishing book review',
    ],
    title: 'Magic book reviews',
    subtitle: 'Honest takes on magic books',
    href: '/magic-book-reviews',
  },
  {
    id: 'magic-conventions',
    tier: 'featured',
    matchSubstrings: [
      'magic conventions',
      'magic convention',
      'vi conventions',
      'vanishing conventions',
    ],
    title: 'Magic conventions',
    subtitle: 'The Session, Magifest & retreats',
    href: '/magic-conventions',
  },
  {
    id: 'the-session',
    tier: 'featured',
    matchSubstrings: ['the session', 'session convention', 'london magic convention'],
    title: 'The Session',
    subtitle: 'London magic convention',
    href: '/magic-conventions/the-session',
  },
  {
    id: 'magifest',
    tier: 'featured',
    matchSubstrings: ['magifest', 'magi fest'],
    title: 'Magifest',
    subtitle: 'Columbus magic convention',
    href: '/magic-conventions/magifest',
  },
  {
    id: 'retreat-greece-2026',
    tier: 'featured',
    matchSubstrings: ['retreat greece', 'the retreat greece', 'greece retreat'],
    title: 'The Retreat — Greece 2026',
    subtitle: 'Luxury magic getaway',
    href: '/magic-conventions/the-retreat-greece-2026',
  },
  {
    id: 'retreat-machu-picchu-2027',
    tier: 'featured',
    matchSubstrings: [
      'retreat machu',
      'machu picchu retreat',
      'the retreat machu',
      'peru retreat',
    ],
    title: 'The Retreat — Machu Picchu 2027',
    subtitle: 'Sacred Valley & Cusco',
    href: '/magic-conventions/the-retreat-machu-picchu-2027',
  },
  {
    id: 'convention-gallery',
    tier: 'featured',
    matchSubstrings: [
      'convention gallery',
      'magic convention gallery',
      'convention photos',
    ],
    title: 'Convention gallery',
    subtitle: 'Photos from our events',
    href: '/magic-conventions/gallery',
  },
  {
    id: 'youth-scholarship-session',
    tier: 'featured',
    matchSubstrings: [
      'youth scholarship',
      'session scholarship',
      'young magician scholarship',
      'scholarship for young',
      'teen scholarship magic',
    ],
    title: 'The Session — Youth scholarships',
    subtitle: 'For young magicians under 18',
    href: '/magic-conventions/the-session/youth-scholarship',
  },
  {
    id: 'magifest-youth',
    tier: 'featured',
    matchSubstrings: [
      'magifest youth',
      'magifest scholarship',
      'magifest young magician',
    ],
    title: 'Magifest — Youth scholarships',
    subtitle: 'Scholarship program for young magicians',
    href: '/magic-conventions/magifest/youth',
  },
  {
    id: 'charity',
    tier: 'featured',
    matchSubstrings: [
      'charity',
      'vanishing charity',
      'vi charity',
      'magic charity',
      '501c3',
      '501(c)3',
    ],
    title: 'Vanishing Inc. Charity',
    subtitle: '501(c)(3) supporting the art of magic',
    href: '/charity',
  },
  {
    id: 'ios-app',
    tier: 'featured',
    matchSubstrings: [
      'ipad app',
      'iphone app',
      'ios app',
      'vanishing inc app',
      'ipad iphone',
    ],
    title: 'Vanishing Inc. iOS app',
    subtitle: 'iPad & iPhone magic app',
    href: '/ipad-iphone',
  },
  {
    id: 'brand-store',
    tier: 'featured',
    matchSubstrings: [
      'brand store',
      'brandstore',
      'vi merch',
      'vanishing merch',
      'vanishing inc brand',
      'official merch',
      'vi apparel',
      'vi hoodie',
    ],
    title: 'Vanishing Inc. Brand Store',
    subtitle: 'Official merch & accessories',
    href: '/brand-store',
  },
  {
    id: 'exclusives',
    tier: 'featured',
    matchSubstrings: [
      'exclusives',
      'vi exclusives',
      'vanishing exclusives',
      'vanishing inc exclusives',
      'produced by vi',
      'vi only',
      'exclusive to vanishing',
    ],
    title: 'Exclusives',
    subtitle: 'Tricks & media produced by Vanishing Inc.',
    href: '/exclusives',
  },
  {
    id: 'deluxe-books',
    tier: 'featured',
    matchSubstrings: [
      'deluxe books',
      'deluxe book',
      'signed magic books',
      'collectible magic books',
    ],
    title: 'Deluxe books',
    subtitle: 'Signed & collectible magic books',
    href: '/deluxe-books',
  },
  {
    id: 'gift-certificates',
    tier: 'featured',
    matchSubstrings: [
      'gift certificate',
      'gift certificates',
      'gift cert',
      'e-gift',
      'egift',
    ],
    title: 'Gift certificates',
    subtitle: 'Give the gift of magic',
    href: '/gift-certificates/',
  },
  {
    id: 'wonders',
    tier: 'featured',
    matchSubstrings: [
      'wonders',
      'lifestyle magic',
      'mindful play',
      'puzzles and brainteasers',
    ],
    title: 'Wonders',
    subtitle: 'Art, puzzles, productivity & home',
    href: '/wonders',
  },

  // --- General (account) — prefer phrases that rarely match product titles ---
  {
    id: 'account-home',
    tier: 'general',
    matchSubstrings: ['account', 'dashboard'],
    title: 'Your account',
    subtitle: 'Overview & shortcuts',
    href: '/account',
  },
  {
    id: 'downloads',
    tier: 'general',
    matchSubstrings: [
      'downloads',
      'library'
    ],
    title: 'Downloads',
    subtitle: 'Digital products you own',
    href: '/account/downloads',
  },
  {
    id: 'orders',
    tier: 'general',
    matchSubstrings: [
      'order',
      'track',
      'invoice'
    ],
    title: 'Orders',
    subtitle: 'View and track your orders',
    href: '/account/orders',
  },
  {
    id: 'invoices',
    tier: 'general',
    matchSubstrings: ['customize invoice', 'customise invoice'],
    title: 'Invoices',
    subtitle: 'Customize invoice details',
    href: '/account/orders/customize-invoice',
  },
  {
    id: 'returns',
    tier: 'general',
    matchSubstrings: [
      'return',
      'returns',
    ],
    title: 'Returns',
    subtitle: 'Return an item',
    href: '/account/orders/returns',
  },
  {
    id: 'newsletters',
    tier: 'general',
    matchSubstrings: [
      'newsletter',
      'email',
    ],
    title: 'Newsletters & email',
    subtitle: 'Manage what we send you',
    href: '/account/newsletters',
  },
  {
    id: 'wishlist',
    tier: 'general',
    matchSubstrings: ['wishlist', 'wish list', 'my wishlist'],
    title: 'Wishlist',
    subtitle: 'Saved items',
    href: '/account/wishlist',
  },
  {
    id: 'subscription',
    tier: 'general',
    matchSubstrings: [
      'subscription',
      'membership',
      'recurring',
    ],
    title: 'Subscriptions',
    subtitle: 'Manage recurring purchases',
    href: '/account/subscription',
  },
  {
    id: 'address',
    tier: 'general',
    matchSubstrings: [
      'address',
      'addresses',
    ],
    title: 'Addresses',
    subtitle: 'Shipping & billing addresses',
    href: '/account/update/address',
  },
  {
    id: 'profile',
    tier: 'general',
    matchSubstrings: [
      'account settings',
      'edit profile',
      'update profile',
      'change password',
      'account details',
    ],
    title: 'Profile & settings',
    subtitle: 'Name, email, password',
    href: '/account/update',
  },
  {
    id: 'points',
    tier: 'general',
    matchSubstrings: [
      'points'
    ],
    title: 'Points',
    subtitle: 'Earn and spend points',
    href: '/account/points',
  },
  {
    id: 'credit',
    tier: 'general',
    matchSubstrings: ['credit'],
    title: 'Store credit',
    subtitle: 'Balance and history',
    href: '/account/credit',
  },
  {
    id: 'reviews',
    tier: 'general',
    matchSubstrings: ['reviews'],
    title: 'Reviews',
    subtitle: 'Products you have reviewed',
    href: '/account/reviews',
  },
  {
    id: 'instructions',
    tier: 'general',
    matchSubstrings: ['instructions'],
    title: 'Video instructions',
    subtitle: 'Access for tricks you own',
    href: '/account/instructions',
  },
  {
    id: 'apps',
    tier: 'general',
    matchSubstrings: ['apps'],
    title: 'Apps',
    subtitle: 'Linked magic apps',
    href: '/account/apps',
  },
  {
    id: 'cards',
    tier: 'general',
    matchSubstrings: ['gift card', 'voucher'],
    title: 'Gift cards',
    subtitle: 'Balances and codes',
    href: '/account/cards',
  },
  {
    id: 'studio',
    tier: 'general',
    matchSubstrings: ['studio', 'streaming'],
    title: 'VI Studio',
    subtitle: 'Your streaming library',
    href: '/account/studio',
  },
  {
    id: 'conventions-account',
    tier: 'general',
    matchSubstrings: ['convention', 'tickets', 'registration'],
    title: 'Convention tickets',
    subtitle: 'Your convention registrations',
    href: '/account/conventions',
  },
  {
    id: 'masterclass-account',
    tier: 'general',
    matchSubstrings: [
      'masterclass',
      'lectures',
      'lecture',
      'lecture library',
      'lecture stream',
      'lecture live',
      'lecture library',
      'lecture stream',
      'lecture live',
      'lecture library',
      'lecture stream',
      'lecture live',
    ],
    title: 'Masterclass',
    subtitle: 'Stream and library access',
    href: '/account/masterclass',
  },
  {
    id: 'checkout',
    tier: 'general',
    matchSubstrings: ['checkout', 'check out', 'shopping cart', 'shopping basket'],
    title: 'Checkout',
    subtitle: 'Complete your order',
    href: '/checkout',
  },
  {
    id: 'faqs',
    tier: 'general',
    matchSubstrings: ['faq', 'faqs', 'frequently asked', 'help center', 'customer help'],
    title: 'FAQs & help',
    subtitle: 'Answers, shipping, returns & policies',
    href: '/faqs',
  },
]

function normalizeQuery(q: string): string {
  return q.trim().toLowerCase().replace(/\s+/g, ' ')
}

function collectMatches(
  promos: SearchAutocompletePromotion[],
  normalizedQuery: string
): SearchAutocompletePromotion[] {
  const seen = new Set<string>()
  const out: SearchAutocompletePromotion[] = []

  for (const promo of promos) {
    if (seen.has(promo.id)) continue
    const hit = promo.matchSubstrings.some(sub =>
      normalizedQuery.includes(sub.toLowerCase())
    )
    if (hit) {
      seen.add(promo.id)
      out.push(promo)
    }
  }

  return out
}

export function getMatchingSearchPromotions(query: string): SearchAutocompletePromotion[] {
  const n = normalizeQuery(query)
  if (!n) return []

  const promos = SEARCH_AUTOCOMPLETE_PROMOTIONS.filter(
    (p) =>
      p.id !== 'retreat-machu-picchu-2027' || isMachuRetreatPubliclyListed()
  )

  const featured = promos.filter(p => p.tier === 'featured')
  const general = promos.filter(p => p.tier === 'general')

  return [...collectMatches(featured, n), ...collectMatches(general, n)]
}
